import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const DoctorPayments = () => {
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [balance, setBalance] = useState(null);

  // Check URL params for success/refresh
  const isSuccess = searchParams.get('success') === 'true';
  const isRefresh = searchParams.get('refresh') === 'true';

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch Stripe Connect status
  useEffect(() => {
    const fetchStripeStatus = async () => {
      if (!token) return;

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/stripe/connect/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch Stripe status');
        }

        const data = await response.json();
        setStripeStatus(data);

        // If connected and charges enabled, fetch balance
        if (data.connected && data.chargesEnabled) {
          fetchBalance();
        }
      } catch (err) {
        console.error('Error fetching Stripe status:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      fetchStripeStatus();
    }
  }, [token, isAuthenticated, isSuccess, isRefresh]);

  // Fetch balance
  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/stripe/connect/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  // Handle Connect Stripe Account button
  const handleConnectStripe = async () => {
    try {
      setConnectingStripe(true);
      setError(null);

      // Step 1: Create Stripe Connect account if not exists
      if (!stripeStatus?.connected) {
        const createResponse = await fetch(`${API_URL}/stripe/connect/create-account`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!createResponse.ok) {
          const errData = await createResponse.json();
          throw new Error(errData.error || 'Failed to create Stripe account');
        }
      }

      // Step 2: Get onboarding link
      const onboardingResponse = await fetch(`${API_URL}/stripe/connect/onboarding-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/doctor/payments?success=true`,
          refreshUrl: `${window.location.origin}/doctor/payments?refresh=true`
        })
      });

      if (!onboardingResponse.ok) {
        const errData = await onboardingResponse.json();
        throw new Error(errData.error || 'Failed to create onboarding link');
      }

      const { url } = await onboardingResponse.json();

      // Redirect to Stripe onboarding
      window.location.href = url;
    } catch (err) {
      console.error('Error connecting Stripe:', err);
      setError(err.message);
      setConnectingStripe(false);
    }
  };

  // Handle open Stripe Dashboard
  const handleOpenDashboard = async () => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/stripe/connect/dashboard-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to create dashboard link');
      }

      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError(err.message);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/doctor/dashboard"
            className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver transition-smooth"
          >
            ← Back to Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Payments
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Manage your payment settings and connect your Stripe account to receive payments
          </p>
        </div>

        {/* Success Message */}
        {isSuccess && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
            <p className="font-body text-green-400">
              ✓ Stripe account connected successfully! You can now receive payments.
            </p>
          </div>
        )}

        {/* Refresh Message */}
        {isRefresh && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="font-body text-yellow-400">
              Your onboarding session expired. Please continue the setup process.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="font-body text-red-400">
              {error}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
            <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
              Loading payment settings...
            </p>
          </div>
        ) : (
          <>
            {/* Stripe Connect Status Card */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
                Stripe Connect Status
              </h2>

              {!stripeStatus?.connected ? (
                /* Not Connected State */
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-silver/10 flex items-center justify-center">
                    <svg className="w-8 h-8 text-silver/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <h3 className="font-display text-2xl italic mb-2">
                    Connect Your Stripe Account
                  </h3>
                  <p className="font-body text-light-text-secondary dark:text-silver/60 mb-6 max-w-md mx-auto">
                    To receive payments from patients, you need to connect a Stripe account.
                    Calendiary uses Stripe Connect for secure, compliant payment processing.
                  </p>
                  <button
                    onClick={handleConnectStripe}
                    disabled={connectingStripe}
                    className="px-8 py-3 bg-gradient-silver text-obsidian font-mono text-sm uppercase tracking-widest rounded-xl hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {connectingStripe ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-obsidian"></div>
                        Connecting...
                      </span>
                    ) : (
                      'Connect Stripe Account'
                    )}
                  </button>
                  <p className="font-body text-xs text-slate-400 dark:text-silver/40 mt-4">
                    You'll be redirected to Stripe to complete the setup
                  </p>
                </div>
              ) : stripeStatus.requiresAction ? (
                /* Incomplete Setup State */
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/10 flex items-center justify-center">
                    <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="font-display text-2xl italic mb-2">
                    Complete Your Setup
                  </h3>
                  <p className="font-body text-light-text-secondary dark:text-silver/60 mb-6 max-w-md mx-auto">
                    Your Stripe account is created but requires additional information.
                    Complete the onboarding to start accepting payments.
                  </p>
                  <button
                    onClick={handleConnectStripe}
                    disabled={connectingStripe}
                    className="px-8 py-3 bg-gradient-silver text-obsidian font-mono text-sm uppercase tracking-widest rounded-xl hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {connectingStripe ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-obsidian"></div>
                        Redirecting...
                      </span>
                    ) : (
                      'Continue Setup'
                    )}
                  </button>
                </div>
              ) : (
                /* Connected State */
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-body font-medium">Stripe Connected</p>
                      <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                        Account ID: {stripeStatus.stripeConnectId}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-slate-100 dark:bg-silver/5 rounded-xl">
                      <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                        Charges
                      </p>
                      <p className={`font-body ${stripeStatus.chargesEnabled ? 'text-green-400' : 'text-red-400'}`}>
                        {stripeStatus.chargesEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-100 dark:bg-silver/5 rounded-xl">
                      <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                        Payouts
                      </p>
                      <p className={`font-body ${stripeStatus.payoutsEnabled ? 'text-green-400' : 'text-red-400'}`}>
                        {stripeStatus.payoutsEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-100 dark:bg-silver/5 rounded-xl">
                      <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                        Status
                      </p>
                      <p className={`font-body ${stripeStatus.detailsSubmitted ? 'text-green-400' : 'text-yellow-400'}`}>
                        {stripeStatus.detailsSubmitted ? 'Complete' : 'Pending'}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleOpenDashboard}
                    className="px-6 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-white/20 transition-smooth"
                  >
                    Open Stripe Dashboard
                  </button>
                </div>
              )}
            </div>

            {/* Balance Card (only if connected) */}
            {stripeStatus?.connected && stripeStatus?.chargesEnabled && (
              <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
                <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
                  Balance
                </h2>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                      Available
                    </p>
                    <p className="font-display text-3xl italic">
                      {balance ? formatCurrency(balance.available?.[0]?.amount || 0, balance.currency) : '£0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                      Pending
                    </p>
                    <p className="font-display text-3xl italic text-light-text-secondary dark:text-silver/60">
                      {balance ? formatCurrency(balance.pending?.[0]?.amount || 0, balance.currency) : '£0.00'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Platform Fee Info */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
                Payment Information
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-light-border dark:border-border">
                  <span className="font-body text-light-text-secondary dark:text-silver/60">Platform Fee</span>
                  <span className="font-mono">1%</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-light-border dark:border-border">
                  <span className="font-body text-light-text-secondary dark:text-silver/60">Payout Schedule</span>
                  <span className="font-mono">Weekly</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="font-body text-light-text-secondary dark:text-silver/60">Currency</span>
                  <span className="font-mono">GBP (£)</span>
                </div>
              </div>
              <p className="font-body text-xs text-slate-400 dark:text-silver/40 mt-4">
                All payments are processed securely through Stripe. A 1% platform fee is deducted from each transaction.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DoctorPayments;
